import React, { useState } from 'react'
import styled from '@emotion/styled'
import { ReactComponent as UploadIcon } from './upload.svg'
import { Button, Dialog, DialogActions, DialogTitle, IconButton, Stack, Typography, useTheme } from '@mui/material'
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined'
import { getColorForDarkMode } from 'utils'
import useCheckMobileScreen from 'hooks/use-check-mobile-screen'
import { COLORS } from 'app-constants'

type Props = {
  shouldRenderDefaultPicture?: boolean
  defaultPicture?: string
  src?: string
  changeLabel: string
  isDisabled?: boolean
  onPicturePicked: React.ChangeEventHandler<HTMLInputElement>
  onPictureRemoved: () => void
  alt: string
  style?: React.CSSProperties
}

const EditablePicture = ({ src, changeLabel, isDisabled, onPicturePicked, onPictureRemoved, alt, style }: Props) => {
  const isMobile = useCheckMobileScreen()
  const theme = useTheme()
  const [isShowingRemoveButton, setIsShowingRemoveButton] = useState(false)
  const [, setIsShowingAddButton] = useState<boolean>(false)
  const onMouseEnter = () => {
    if (!src) {
      setIsShowingAddButton(true)
    } else {
      setIsShowingRemoveButton(true)
    }
  }

  const onMouseLeave = () => {
    setIsShowingAddButton(false)
    setIsShowingRemoveButton(false)
  }

  const [isRemoveDialogOpen, setIsRemoveDialogOpen] = useState(false)
  const onRemoveClicked = () => {
    setIsRemoveDialogOpen(true)
  }

  const renderConfirmRemoveDialog = () => {
    return (
      <Dialog open={isRemoveDialogOpen}>
        <DialogTitle>Are you sure you want to remove the current {changeLabel}?</DialogTitle>
        <DialogActions>
          <Button
            onClick={() => {
              setIsRemoveDialogOpen(false)
            }}
            sx={{
              color: getColorForDarkMode(theme),
            }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => {
              setIsRemoveDialogOpen(false)
              onPictureRemoved()
            }}>
            Remove
          </Button>
        </DialogActions>
      </Dialog>
    )
  }

  return (
    <Stack spacing={1}>
      <Typography
        mb="10px"
        variant="h7.25"
        sx={{
          color: COLORS.GRAY_2,
          textAlign: 'left',
          mt: 1.5,
        }}>
        Upload a profile picture<span style={{ color: COLORS.RED }}>*</span>
      </Typography>

      <ContainerDiv
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        className={src ? 'hasSrc' : 'hasFallback'}
        style={style}>
        {src ? (
          <Img src={src} alt={alt} referrerPolicy="no-referrer" />
        ) : (
          <UploadIcon
            style={{
              color: COLORS.GRAY_3,
              height: '36px',
              width: '36px',
            }}
          />
        )}

        {isShowingRemoveButton || (isMobile && src) ? (
          <RemoveDiv>
            <IconButton onClick={onRemoveClicked} style={{ color: 'white' }}>
              <DeleteOutlinedIcon width={36} />
            </IconButton>
          </RemoveDiv>
        ) : (
          <AddDiv>
            <Input
              accept="image/*"
              id="contained-button-file"
              type="file"
              onChange={onPicturePicked}
              disabled={isDisabled}
              style={{
                height: '100%',
                width: '100%',
              }}
            />
          </AddDiv>
        )}
      </ContainerDiv>
      {renderConfirmRemoveDialog()}
    </Stack>
  )
}

const ContainerDiv = styled.div`
  position: relative;
  height: 160px;
  width: 160px;
  margin-top: 8px;
  display: flex;
  align-items: center;
  justify-content: center;

  overflow: hidden;
  border-radius: 50%;

  &.hasSrc {
    border: 1px solid #bbbbbb;
    background: white;
  }

  &.hasFallback {
    background: #eee;
    border: 1px solid #ddd;
  }
`

const AddDiv = styled.div`
  position: absolute;
  top: 0px;
  left: 0px;
  right: 0px;
  bottom: 0px;
  display: flex;
  align-items: center;
  justify-content: center;
`

const RemoveDiv = styled.div`
  position: absolute;
  top: 0px;
  left: 0px;
  right: 0px;
  bottom: 0px;
  display: flex;
  align-items: center;
  justify-content: center;

  background: #000000;
  opacity: 0.5;
`

const Img = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`

const Input = styled('input')({
  opacity: 0,
})

export default EditablePicture
